import React from 'react';

import { useFormContext } from 'react-hook-form';

import { Collapse, Divider, Grid, Typography } from '@mui/material';

import ControlledTextField from '../../../components/ControlledTextField';
import ControlledPeoplePicker from '../../../components/ControlledPeoplePicker';
import ControlledAirportSelect from '../../../components/ControlledAirportSelect';

import { Employee, EmployeePosition, FlightEventReport, FormGenerator } from '../../../core/api/clients';
import { useFlightEventFieldOptions } from '../../../core/context/FlightEventFieldOptionsContext';
import { useFlightEventReportData } from '../../../core/context/FlightEventReportDataContext';
import ControlledAircraftSelect from '../../../components/ControlledAircraftSelect';

import { transformToUtcTime } from '../../../core/textTransforms';

function FlightStep({ visible, readOnly, isRevenue }:
{ visible: boolean, readOnly?: boolean, isRevenue?: boolean }) {
  const { airports, aircrafts } = useFlightEventFieldOptions();
  const { flightEventReport } = useFlightEventReportData();
  const { control, setValue, resetField } = useFormContext<FlightEventReport>();

  const { allowedActions } = useFlightEventReportData();

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <ControlledTextField<FlightEventReport>
            name="flightNumber"
            control={control}
            rules={{ required: flightEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledAircraftSelect<FlightEventReport>
            name="aircraftNumber"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={aircrafts ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<FlightEventReport>
            name="departureAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<FlightEventReport>
            name="scheduledArrivalAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<FlightEventReport>
            name="landingAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        {flightEventReport.pushTime && (
          <React.Fragment>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">OUT: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(flightEventReport.pushTime)}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">OFF: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(flightEventReport.offTime)}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">ON: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(flightEventReport.onTime)}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">IN: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(flightEventReport.inTime)}</Typography>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ControlledPeoplePicker<FlightEventReport>
            groupName="captain"
            name="captain"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['captainId', 'captain', 'captainDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('captain', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: flightEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<FlightEventReport>
            groupName="firstOfficer"
            name="firstOfficer"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['firstOfficerId', 'firstOfficer', 'firstOfficerDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('firstOfficer', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: flightEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<FlightEventReport>
            groupName="flightAttendant"
            name="flightAttendant"
            position={EmployeePosition.InFlight}
            control={control}
            fieldNames={['flightAttendantId', 'flightAttendant', 'flightAttendantDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('flightAttendant', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: isRevenue && flightEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
}

export default FlightStep;
